import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Menu,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconArchive,
  IconArchiveOff,
  IconDots,
  IconTrash,
  IconAlertCircle,
  IconFolderBolt,
  IconKey,
  IconUserEdit,
} from "@tabler/icons-react";
import type { TeamMemberRowProps } from "../types";
import { RETURN_LABEL } from "@pages/Credits/constants";

/**
 * Team Member Row Component
 *
 * Displays a single team member with their information, credit allocation,
 * and inline action buttons for credit management.
 *
 * Features:
 * - Member name, email, and status badge
 * - Credit allocation and access type display
 * - Credit request warning badge
 * - Inline action buttons: Set Credits, Edit Access
 * - More menu: Archive/Unarchive, Delete (pending)
 */
export const TeamMemberRow = ({
  member,
  onSetCredits,
  onEditAccessType,
  onChangeRole,
  onArchive,
  onDelete,
  showCreditActions = true,
}: TeamMemberRowProps) => {
  const getStatusColor = (status: string) => {
    if (status === "active") return "green";
    if (status === "pending") return "blue";
    return "gray";
  };

  const getAccessTypeColor = (businessUse: boolean) => {
    return businessUse ? "cyan" : "grape";
  };

  const getAccessTypeLabel = (businessUse: boolean) => {
    return businessUse ? "Business Use" : "Limited Use";
  };

  const getAccessTypeTooltip = (businessUse: boolean) => {
    return businessUse
      ? "Can use returns for any client in the business"
      : "Can only use returns for their assigned clients";
  };

  return (
    <>
      {/* Header Row: Name + Status + Menu */}
      <Group justify="space-between" wrap="nowrap">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="md" fw={600} truncate style={{ color: "#212529" }}>
              {member.firstName && member.lastName
                ? `${member.firstName} ${member.lastName}`
                : member.firstName || "New Member"}
            </Text>
            {member.creditRequest && (
              <Badge
                variant="light"
                color="yellow"
                size="xs"
                leftSection={<IconAlertCircle size={10} />}
                fw={700}
              >
                Requested
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed" truncate fw={500}>
            {member.email}
          </Text>
        </Box>

        <Group gap="xs" wrap="nowrap">
          <Badge
            variant="light"
            color={getStatusColor(member.status)}
            radius="sm"
            size="sm"
          >
            {member.status || "pending"}
          </Badge>

          {/* More Menu */}
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              {/* Credit Actions (for active non-owners) */}
              {showCreditActions &&
                member.role !== "owner" &&
                member.status === "active" && (
                  <>
                    <Menu.Item
                      leftSection={<IconFolderBolt size={16} />}
                      onClick={() => onSetCredits(member.userId)}
                    >
                      Set Credits
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconKey size={16} />}
                      onClick={() => onEditAccessType(member.userId)}
                    >
                      Edit Access
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconUserEdit size={16} />}
                      onClick={() => onChangeRole(member.userId)}
                    >
                      Change Role
                    </Menu.Item>
                    <Menu.Divider />
                  </>
                )}

              {/* Archive/Delete Actions */}
              {member.status === "pending" ? (
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => onDelete(member.userId)}
                >
                  Delete invite
                </Menu.Item>
              ) : member.role !== "owner" ? (
                <Menu.Item
                  leftSection={
                    member.status === "inactive" ? (
                      <IconArchiveOff size={16} />
                    ) : (
                      <IconArchive size={16} />
                    )
                  }
                  onClick={() =>
                    onArchive(member.userId, member.status === "active")
                  }
                >
                  {member.status === "inactive" ? "Unarchive" : "Archive"}
                </Menu.Item>
              ) : null}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {/* Credit Info (for active non-owners only) - pushed to bottom */}
      {showCreditActions &&
        member.role !== "owner" &&
        member.status !== "pending" && (
          <Box className="credit-info-section">
            <Group gap="xs">
              <Text size="sm" fw={600} c="#005fa3">
                {member.allocatedCredits} {RETURN_LABEL}s
              </Text>
              <Text size="xs" c="dimmed">
                |
              </Text>
              <Tooltip
                label={getAccessTypeTooltip(member.businessUse)}
                position="top"
                withArrow
              >
                <Badge
                  variant="dot"
                  size="sm"
                  color={getAccessTypeColor(member.businessUse)}
                  style={{ cursor: "help" }}
                >
                  {getAccessTypeLabel(member.businessUse)}
                </Badge>
              </Tooltip>
            </Group>
          </Box>
        )}
    </>
  );
};
